import { COLUMN_SELECT_ID } from "@/constants";
import { Header, Row, Table } from "@tanstack/react-table";
import { TFunction } from "i18next";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CellAddress } from "../utils/cellAddress";
import { escapeHtml, getCellHtmlValue } from "../utils/getCellHtmlValue";
import { getHeaderTextValue } from "../utils/getHeaderTextValue";
import {
  getPathBuilderLinkResolver,
  LinkResolver,
} from "../utils/getPathBuilderLinkResolver";

const HEADER_ROW_INDEX = -1;

interface GridCell {
  text: string;
  html: string;
}

// ---------------------------------------------------------------------------
// Grid building
// ---------------------------------------------------------------------------

function buildHeaderRow<TData>(
  headers: Header<TData, unknown>[],
  columnIds: string[],
  t: TFunction,
): string[] {
  const headerMap = new Map(headers.map((h) => [h.column.id, h]));
  return columnIds.map((colId) => {
    const header = headerMap.get(colId);
    return header ? getHeaderTextValue(header, t) : "";
  });
}

function buildGrid<TData>(
  rows: Row<TData>[],
  columnIds: string[],
  minRow: number,
  maxRow: number,
  linkResolver: LinkResolver<TData> | null,
): GridCell[][] {
  const grid: GridCell[][] = [];
  for (let r = minRow; r <= maxRow; r++) {
    const row = rows[r];
    if (!row) continue;

    const cells = row.getVisibleCells();
    const cellMap = new Map(cells.map((cell) => [cell.column.id, cell]));

    grid.push(
      columnIds.map((colId) => {
        const cell = cellMap.get(colId);
        if (!cell) return { text: "", html: "" };

        const fallbackUrl = linkResolver?.(row, colId) ?? null;
        return getCellHtmlValue(cell, fallbackUrl);
      }),
    );
  }
  return grid;
}

// ---------------------------------------------------------------------------
// Output formatting
// ---------------------------------------------------------------------------

function gridToTsv(grid: GridCell[][], headerRow?: string[]): string {
  const rows: string[][] = [];
  if (headerRow) rows.push(headerRow);
  for (const row of grid) {
    rows.push(row.map((c) => c.text));
  }

  return rows
    .map((row) =>
      row.map((v) => v.replace(/\t/g, " ").replace(/\n/g, " ")).join("\t"),
    )
    .join("\n");
}

function buildHtmlCell(
  tag: "td" | "th",
  content: string,
  isHtml: boolean,
): string {
  const style = `border:1px solid #ccc;padding:4px 8px${tag === "th" ? ";font-weight:bold" : ""}`;
  const safe = isHtml ? content : escapeHtml(content);
  return `<${tag} style="${style}">${safe}</${tag}>`;
}

function gridToHtml(grid: GridCell[][], headerRow?: string[]): string {
  const htmlRows: string[] = [];

  if (headerRow) {
    const cells = headerRow.map((text) => buildHtmlCell("th", text, false));
    htmlRows.push(`<tr>${cells.join("")}</tr>`);
  }

  for (const row of grid) {
    const cells = row.map((c) => buildHtmlCell("td", c.html, true));
    htmlRows.push(`<tr>${cells.join("")}</tr>`);
  }

  return `<table style="border-collapse:collapse">${htmlRows.join("")}</table>`;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseCopyToClipboardParams<TData> {
  table: Table<TData>;
  visibleColumnOrder: string[];
  getSelectionBounds: () => {
    start: CellAddress | null;
    end: CellAddress | null;
  };
}

export interface CopyData {
  tsv: string;
  html: string;
}

export default function useCopyToClipboard<TData>({
  table,
  visibleColumnOrder,
  getSelectionBounds,
}: UseCopyToClipboardParams<TData>) {
  const { t } = useTranslation();

  const buildSelectionData = useCallback((): CopyData | null => {
    const { start, end } = getSelectionBounds();

    const isNothingSelected = !start || !end;

    if (isNothingSelected) {
      return null;
    }

    const isJustSingleCellSelected =
      start.rowIndex === end.rowIndex && start.columnId === end.columnId;

    // Single-cell selection: let native copy handle it
    // (user may want to copy partial text within a cell)
    if (isJustSingleCellSelected) {
      return null;
    }

    const rows = table.getRowModel().rows;

    if (rows.length === 0) {
      return null;
    }

    const copyableColumns = visibleColumnOrder.filter(
      (id) => id !== COLUMN_SELECT_ID,
    );
    const startColIndex = copyableColumns.indexOf(start.columnId);
    const endColIndex = copyableColumns.indexOf(end.columnId);

    const isColumnNotFound = startColIndex === -1 || endColIndex === -1;

    if (isColumnNotFound) {
      return null;
    }

    const minCol = Math.min(startColIndex, endColIndex);
    const maxCol = Math.max(startColIndex, endColIndex);
    const selectedColumnIds = copyableColumns.slice(minCol, maxCol + 1);

    const minRow = Math.min(start.rowIndex, end.rowIndex);
    const maxRow = Math.max(start.rowIndex, end.rowIndex);

    const linkResolver = getPathBuilderLinkResolver(table);

    const includesHeader = minRow === HEADER_ROW_INDEX;
    const dataMinRow = Math.max(minRow, 0);
    const grid = buildGrid(
      rows,
      selectedColumnIds,
      dataMinRow,
      maxRow,
      linkResolver,
    );

    let headerRow: string[] | undefined;

    if (includesHeader) {
      const headers = table.getHeaderGroups()[0]?.headers ?? [];
      headerRow = buildHeaderRow(headers, selectedColumnIds, t);
    }

    if (grid.length === 0 && !headerRow) {
      return null;
    }

    return {
      tsv: gridToTsv(grid, headerRow),
      html: gridToHtml(grid, headerRow),
    };
  }, [table, visibleColumnOrder, getSelectionBounds, t]);

  const handleCopy = useCallback(
    (event: ClipboardEvent) => {
      const data = buildSelectionData();
      if (!data) {
        return;
      }

      event.preventDefault();
      event.clipboardData?.setData("text/plain", data.tsv);
      event.clipboardData?.setData("text/html", data.html);
    },
    [buildSelectionData],
  );

  return { handleCopy, buildSelectionData };
}
